import { ArrowRight, ChevronDown, ChevronUp, Info, Lock, ShoppingBag } from 'react-feather';
import { Trans, useTranslation } from 'react-i18next';
import { useContext, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import { CheckoutStep } from './helpers';
import { EmbedContext } from '../Common/EmbedProvider';
import { containsKeyPrefix, filterServerErrors } from '../../common/helpers';
import { useCheckoutSummary } from './CheckoutSummaryProvider';
import { useEvent } from '../Common/EventProvider';
import { useTracking } from '../../common/Tracking';
import CheckoutContext from './CheckoutContext';
import CheckoutSummary from './CheckoutSummary';
import PagerContainer from '../Common/PagerContainer';
import UI from '../../common/UI';
import config from '../config';
import useLocale from '../../common/useLocale';
import useMediaDevice from '../../common/useMediaDevice';
import useStickyElement from '../../common/useStickyElement';

export interface PagerProps {
  checkoutSteps: CheckoutStep[];
  activeStepIndex: number;
  loading?: boolean;
  slowFetchTimeout?: number
}

/**
 * Shows the Next button and shopping basket. During personalisation, it also shows a Previous button.
 * In the last step, it shows the Pay/Register button.
 */
const Pager = ({ checkoutSteps, activeStepIndex, loading, slowFetchTimeout = 3000 }: PagerProps) => {
  const { t } = useTranslation();
  const { formatCurrency, formatNumber, locale } = useLocale();
  const { embedded } = useContext(EmbedContext);
  const { track } = useTracking();
  const device = useMediaDevice();

  const { event } = useEvent();
  const { checkoutSummary, loading: refetching } = useCheckoutSummary();

  const activeStepName = checkoutSteps[activeStepIndex];

  const {
    form,
    validators,
    personalisation,
  } = useContext(CheckoutContext);

  const validator = validators[activeStepName];

  const valid = activeStepName === CheckoutStep.Personalisation ? (
    !containsKeyPrefix(filterServerErrors(validator.errors), `registrations.create.${personalisation.activeFormIndex}`)
    && validator.validating.filter((attribute) => attribute.startsWith(`registrations.create.${personalisation.activeFormIndex}`)).length === 0
  ) : validator.valid;

  const quantity = checkoutSummary.tickets.reduce((sum, purchase) => sum + purchase.quantity, 0);

  const { anchorRef, elementRef, isSticky, position, reset: resetStickyElement } = useStickyElement({
    position: 'bottom',
    containerId: 'CheckoutContainer',
    scrollContainerId: embedded && !device.isSmall ? 'PageContainer' : undefined,
  });

  const showTotals = activeStepName !== CheckoutStep.Payment;

  const [showSummary, setShowSummary] = useState(false);

  const amount = ((form.down_payment ? checkoutSummary.down_payment_amount : checkoutSummary.amount) || 0)
    + checkoutSummary.passed_on_fee;

  useEffect(() => {
    resetStickyElement();
  }, [showSummary, showTotals, resetStickyElement]);

  const [showSlowFetchAlert, setShowSlowFetchAlert] = useState(false);

  const unassignedTicketCount = form.registrations.create.filter((registration, index) => (
    !registration.participant || !personalisation.personalisedFormIndices.includes(index)
  )).length;

  useEffect(() => {
    if (refetching) {
      // If fetching the checkout summary takes more than 2 seconds, show a message to the user.
      const timer = window.setTimeout(() => {
        setShowSlowFetchAlert(true);
      }, slowFetchTimeout);

      return () => window.clearTimeout(timer);
    }

    setShowSummary(false);
    setShowSlowFetchAlert(false);

    return undefined;
  }, [refetching, slowFetchTimeout]);

  const toggleSummary = () => {
    setShowSummary(!showSummary);

    track?.('Checkout:SummaryToggled', {
      showSummary: !showSummary,
    });
  };

  return (
    <UI.Div sc={{ padding: 0 }} ref={anchorRef}>
      <PagerContainer
        ref={elementRef}
        isSticky={isSticky && showTotals && quantity > 0}
        isEmbedded={embedded}
        isExpanded={showSummary}
        {...position}
      >
        <UI.GridContainer>
          {!showTotals && (
            <>
              {unassignedTicketCount > 0 && (
                <UI.Info icon={<Info />}>
                  <UI.Div>
                    <UI.Strong sc={{ block: true }}>
                      {t('unassigned_tickets_warning')}
                    </UI.Strong>
                    {t('pager.unassigned_tickets_description', { count: unassignedTicketCount })}
                  </UI.Div>
                </UI.Info>
              )}
              <UI.Div>
                <Trans
                  i18nKey="common:terms.terms_notice"
                  values={{
                    action: amount > 0 ? t('pay') : t('register'),
                  }}
                >
                  <UI.A href={config.termsUrl[locale]} target="_blank" rel="noopener noreferer" />
                </Trans>
              </UI.Div>
            </>
          )}
          {checkoutSummary && showSummary && showTotals && (
            <UI.FadeIn>
              <CheckoutSummary event={event} summary={checkoutSummary} loading={refetching} />
            </UI.FadeIn>
          )}
          <UI.FlexContainer sc={{ alignItems: 'center', justifyContent: 'space-between' }}>
            {showSlowFetchAlert && showTotals && (
              <UI.Div>
                <UI.Icon>
                  <UI.Loader />
                </UI.Icon>
                {' '}
                {t('slow_fetch_message')}
              </UI.Div>
            )}
            {!showSlowFetchAlert && showTotals && (
              <UI.Div sc={{ muted: refetching }}>
                <CartButton
                  onClick={toggleSummary}
                  sc={{ disabled: quantity === 0 }}
                  aria-label={t('pager.basket', { quantity: formatNumber(quantity), amount: formatCurrency(amount) })}
                  role="button"
                >
                  <UI.Icon sc={{ mr: 1, big: true }}>
                    <ShoppingBag />
                  </UI.Icon>
                  <UI.Span sc={{ fontWeight: 500, mr: 1 }}>
                    <UI.Delimit>
                      {t('pager.n_tickets', { count: quantity, value: formatNumber(quantity) })}
                      {formatCurrency(amount)}
                    </UI.Delimit>
                  </UI.Span>
                  <UI.Icon sc={{ muted: true, big: true }}>
                    {showSummary ? <ChevronDown /> : <ChevronUp />}
                  </UI.Icon>
                </CartButton>
              </UI.Div>
            )}
            <UI.Div sc={{ width: !showTotals ? '100%' : 'auto' }}>
              <UI.Button
                type="submit"
                sc={{
                  brand: 'secondary',
                  block: !showTotals,
                  loading: loading || refetching,
                  noWrap: true,
                }}
                disabled={loading || refetching || !valid}
                key={activeStepName}
              >
                {activeStepName === CheckoutStep.Payment ? (
                  <>
                    <UI.Icon>
                      {(loading || showSlowFetchAlert) ? (
                        <UI.Loader sc={{ brand: 'white' }} />
                      ) : (
                        <Lock />
                      )}
                    </UI.Icon>
                    &nbsp;
                    {amount > 0 ? t('pay') : t('pager.confirm')}
                  </>
                ) : (
                  <>
                    <span>
                      {activeStepName === CheckoutStep.Tickets ? (
                        t('pager.register')
                      ) : (
                        activeStepName === CheckoutStep.Fundraising && !form.charity && !event.require_fundraising
                          ? t('pager.skip')
                          : t('pager.next')
                      )}
                      {' '}
                    </span>
                    &nbsp;
                    <UI.Icon>
                      <ArrowRight />
                    </UI.Icon>
                  </>
                )}
              </UI.Button>
            </UI.Div>
          </UI.FlexContainer>
        </UI.GridContainer>
      </PagerContainer>
    </UI.Div>
  );
};

const CartButton = styled(UI.A)`
  ${({ theme }) => css`
    color: ${theme.colors.gray[800]};

    &:hover, &:hover ${UI.Badge} {
      color: ${theme.colors.gray[500]};
    }
  `}
`;

export default Pager;
